<script lang="tsx" setup>
import FilterContainer from '@/components/filter/FilterContainer.vue'
import { useI18n } from 'vue-i18n'
import TextFilter from '@/components/filter/TextFilter.vue'
import MultiSelectFilter from '@/components/filter/MultiSelectFilter.vue'
import { selectItem, TextField, ValueField } from '@/models/select-item'
import { getInfoSecCoreText, getTreatmentText } from '@/services/utils'
import useRiskColumns from './columns'
import ResponsibleFilter from '../filter/ResponsibleFilter.vue'
import CalendarFilter from '../filter/CalendarFilter.vue'
import RiskFilter from '../filter/RiskFilter.vue'
import InstanceFilter from '../filter/InstanceFilter.vue'
import RiskCatalogFilter from '../filter/RiskCatalogFilter.vue'
import RiskCategoryFilter from '../filter/RiskCategoryFilter.vue'
import { RT_Avoid, RT_Exclude, RT_Preserve, RT_Reduce, RT_Share, RT_Unscored } from '@/models/enums-generated'
import { onBeforeMount, ref } from 'vue'
import ScoreFilter from '../filter/ScoreFilter.vue'

const { t } = useI18n()
const { columns, custom, all } = useRiskColumns()
const catalogFilterRef = ref<InstanceType<typeof RiskCatalogFilter>>()
const isKeyOfColumns = (key: string): key is keyof typeof columns => key in columns

const setFilterFromParams = () => {
	const params = new URLSearchParams(window.location.search)
	for (const [key, value] of params) {
		// Key doesn't exist in columns or not a filter param.
		if (!isKeyOfColumns(key)) {
			continue
		}
		const column = columns[key].value
		if (key === 'dueDate' || key === 'lastReview') {
			column.filter.push(value)
		} else if (key === 'responsible') {
			const parsed = JSON.parse(value)
			column.filter.push({ id: parsed.id, type: parsed.type })
		} else {
			const parsedNumber = parseInt(value)
			if (!isNaN(parsedNumber)) {
				column.filter.push(parsedNumber)
			} else {
				column.filter.push(value)
			}
		}
	}
}

onBeforeMount(() => {
	setFilterFromParams()
})
defineExpose({ catalogFilterRef })
</script>

<template>
	<FilterContainer :columns="all" :custom="custom" bookmark="threats-bookmark">
		<div class="p-2 filter-col" v-if="columns.code.value.isVisible">
			<TextFilter data-test="risks-filter-code" v-model="columns.code.value"></TextFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.name.value.isVisible">
			<RiskFilter id="risks-filter-name" v-model="columns.name.value"></RiskFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.responsible.value.isVisible">
			<ResponsibleFilter id="risks-filter-responsible" v-model="columns.responsible.value"></ResponsibleFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.cia.value.isVisible">
			<MultiSelectFilter
				id="risks-filter-cia"
				v-model="columns.cia.value"
				:label-field="TextField"
				:value-field="ValueField"
				:formatter="getInfoSecCoreText"
				:options="[
					{ text: t('confidentiality'), value: 'confidentiality' },
					{ text: t('integrity'), value: 'integrity' },
					{ text: t('availability'), value: 'availability' },
				]"
			></MultiSelectFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.dueDate.value.isVisible">
			<CalendarFilter
				id="updated-at-filter-methods"
				v-model="columns.dueDate.value"
				:label-field="TextField"
				:value-field="ValueField"
			></CalendarFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.comment.value.isVisible">
			<TextFilter id="comment-filter-risk" v-model="columns.comment.value"></TextFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.description.value.isVisible">
			<TextFilter id="description-filter-risk" v-model="columns.description.value"></TextFilter>
		</div>

		<div class="p-2 filter-col" v-show="columns.instance.value.isVisible">
			<InstanceFilter id="instance-filter-threat" v-model="columns.instance.value"></InstanceFilter>
		</div>
		<div class="p-2 filter-col" v-if="columns.catalog.value.isVisible">
			<RiskCatalogFilter id="catalog-filter-risk" v-model="columns.catalog.value" ref="catalogFilterRef"></RiskCatalogFilter>
		</div>

		<div class="p-2 filter-col" v-show="columns.category.value.isVisible">
			<RiskCategoryFilter id="category-filter-threat" v-model="columns.category.value"></RiskCategoryFilter>
		</div>

		<div class="p-2 filter-col" v-show="columns.treatment.value.isVisible">
			<MultiSelectFilter
				id="risks-filter-treatment"
				v-model="columns.treatment.value"
				:label-field="TextField"
				:value-field="ValueField"
				:formatter="getTreatmentText"
				:options="[
					selectItem('risk.treatment.unscored', RT_Unscored),
					selectItem('risk.treatment.preserve', RT_Preserve),
					selectItem('risk.treatment.share', RT_Share),
					selectItem('risk.treatment.reduce', RT_Reduce),
					selectItem('risk.treatment.avoid', RT_Avoid),
					selectItem('risk.treatment.exclude', RT_Exclude),
				]"
			></MultiSelectFilter>
		</div>
		<div class="p-2 filter-col" v-show="columns.score.value.isVisible">
			<ScoreFilter id="score-filter-threat" v-model="columns.score.value"></ScoreFilter>
		</div>
		<div class="p-2 filter-col" v-show="columns.residual.value.isVisible">
			<ScoreFilter id="residual-filter-threat" v-model="columns.residual.value"></ScoreFilter>
		</div>
	</FilterContainer>
</template>
